import { useCallback, useEffect, useState } from "react";

import { ApiError, apiGet } from "../api";
import { formatReconciledMinor } from "../money";

/**
 * ANA-01 — the ops console's reconciled read surface. Same
 * `app/services/analytics/rollups.py` functions the merchant admin and
 * vendor portal/native read, scoped to the operator's own shop
 * (`get_current_console_user`). See `app/api/console_portal/analytics.py`.
 */

interface CurrencyRow {
  environment: string;
  currency: string;
  exponent: number;
  amount_minor: string;
  party_role?: string;
  party_ref?: string;
  order_count?: number;
}
interface Coverage {
  window_order_count: number;
  ledger_posted_order_count: number;
  coverage_fraction: number | null;
  state: string;
}
interface Rollup {
  metric_id: string;
  label: string;
  source: string;
  window: { period: string; grain: string };
  coverage: Coverage;
  rows: CurrencyRow[] | null;
  action_owner: { role: string; note: string };
}

const METRICS = ["gmv_ledger", "commission_ledger", "vendor_payable_ledger", "order_ledger_coverage"] as const;
const PERIODS = ["7d", "30d", "90d", "365d"];

function MetricCard({ metric }: { metric: Rollup | null }) {
  if (!metric) {
    return <div className="card">Unable to load this metric.</div>;
  }
  return (
    <div className="card">
      <div className="card-cust">{metric.label}</div>
      <p className="risk-sub">{metric.source}</p>
      {metric.rows === null ? (
        <p className="risk-sub">Not shown — {metric.coverage.state}. Never approximated.</p>
      ) : metric.rows.length === 0 ? (
        <p className="risk-sub">No ledger-posted activity in this window.</p>
      ) : (
        <ul>
          {metric.rows.map((row, i) => (
            <li key={i}>
              {row.party_ref ? `${row.party_role} ${row.party_ref.slice(0, 8)}…` : row.currency}
              {": "}
              <strong>{formatReconciledMinor(row.amount_minor, row.exponent, row.currency)}</strong>
            </li>
          ))}
        </ul>
      )}
      <div className="card-foot">
        <span className="chip chip-cod">
          {metric.coverage.ledger_posted_order_count}/{metric.coverage.window_order_count} orders posted
          {metric.coverage.coverage_fraction === null ? "" : ` (${Math.round(metric.coverage.coverage_fraction * 100)}%)`}
        </span>
      </div>
      <p className="risk-sub">
        Action owner: <strong>{metric.action_owner.role}</strong> — {metric.action_owner.note}
      </p>
    </div>
  );
}

export default function Reconciled() {
  const [period, setPeriod] = useState("30d");
  const [metrics, setMetrics] = useState<Record<string, Rollup | null>>({});
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const results = await Promise.all(
        METRICS.map((id) => apiGet<Rollup>(`/reconciled/${id}?period=${period}`).catch(() => null))
      );
      const next: Record<string, Rollup | null> = {};
      METRICS.forEach((id, i) => { next[id] = results[i]; });
      setMetrics(next);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load reconciled analytics");
    }
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Reconciled analytics</h1>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          {PERIODS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>
      {err && <div className="banner-err">{err}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "10px" }}>
        {METRICS.map((id) => (
          <MetricCard key={id} metric={metrics[id] ?? null} />
        ))}
      </div>
    </div>
  );
}
